/**
 * Hook pour la gestion de session
 *
 * ARCHITECTURE SIMPLIFIÉE (Janvier 2026):
 * - Access token: mémoire via tokenManager
 * - Refresh token: cookie HttpOnly (géré par le backend)
 * - User data: authStore Zustand
 *
 * Ce hook gère:
 * - Refresh automatique avant expiration
 * - Avertissement d'expiration
 * - Logout
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  setAccessToken,
  getAccessToken,
  getTimeUntilExpiry,
  clearSession,
} from '../services/tokenManager';
import { useAuthStore } from '../stores/authStore';
import { logger } from '../utils/logger';

// Configuration
const WARNING_THRESHOLD_SECONDS = 60; // Afficher l'avertissement 1 minute avant expiration
const REFRESH_THRESHOLD_SECONDS = 300; // Rafraîchir 5 minutes avant expiration

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

export interface SessionState {
  isAuthenticated: boolean;
  user: any;
  showWarning: boolean;
  timeUntilExpiry: number;
}

export function useSession() {
  const { user, isAuthenticated, setUser, logout: storeLogout } = useAuthStore();

  const [showWarning, setShowWarning] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(getTimeUntilExpiry());
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRefreshingRef = useRef(false);

  /**
   * Refresh access token using HttpOnly cookie
   * Le cookie est envoyé automatiquement par le navigateur
   */
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/core/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Envoie le cookie HttpOnly automatiquement
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 409) {
          logger.warn(`Refresh token error (${response.status}), session expired`);
          clearSession();
          storeLogout();
          return false;
        }
        throw new Error(`Failed to refresh token: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data?.accessToken) {
        const expiresIn = data.data.expiresIn || 900;
        setAccessToken(data.data.accessToken, expiresIn);
        setTimeUntilExpiry(expiresIn);
        setWarningDismissed(false);
        setRefreshFailed(false);
        setShowWarning(false);

        logger.log('Token refreshed successfully');
        return true;
      }

      setRefreshFailed(true);
      return false;
    } catch (error) {
      logger.error('Failed to refresh token:', error);
      setRefreshFailed(true);
      return false;
    }
  }, [storeLogout]);

  /**
   * Set session after login
   * Note: Le refresh token est dans le cookie, on ne le stocke pas
   */
  const setSession = useCallback(
    (accessToken: string, _refreshToken: string, expiresIn: number, userData: any) => {
      setAccessToken(accessToken, expiresIn);
      setUser(userData);
      setTimeUntilExpiry(expiresIn);
      setWarningDismissed(false);
      setRefreshFailed(false);
      setShowWarning(false);
    },
    [setUser]
  );

  /**
   * Logout user
   */
  const logout = useCallback(async (skipApiCall = false): Promise<void> => {
    if (!skipApiCall) {
      try {
        const accessToken = getAccessToken();

        if (accessToken) {
          await fetch(`${API_BASE}/core/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
          });
        }
      } catch (error) {
        logger.error('Logout API call failed:', error);
      }
    }

    clearSession();
    storeLogout();
    setShowWarning(false);
    setTimeUntilExpiry(0);
  }, [storeLogout]);

  /**
   * Dismiss the warning modal
   */
  const dismissWarning = useCallback(() => {
    setWarningDismissed(true);
    setShowWarning(false);
  }, []);

  /**
   * Send heartbeat (no-op for now)
   */
  const sendHeartbeat = useCallback(async (): Promise<void> => {
    // Token refresh handles keepalive
  }, []);

  /**
   * Check session status periodically
   */
  useEffect(() => {
    const checkSession = () => {
      const token = getAccessToken();

      // Token absent ou expiré — tenter un refresh si l'utilisateur était authentifié
      if (!token) {
        if (isAuthenticated && !isRefreshingRef.current) {
          isRefreshingRef.current = true;
          logger.log('🔄 useSession: Token expiré, tentative de refresh via cookie...');
          refreshAccessToken().finally(() => {
            isRefreshingRef.current = false;
          });
        }
        return;
      }

      const remaining = getTimeUntilExpiry();
      setTimeUntilExpiry(remaining);

      // Auto-refresh if close to expiry
      if (remaining <= REFRESH_THRESHOLD_SECONDS && remaining > 0 && !isRefreshingRef.current) {
        isRefreshingRef.current = true;
        refreshAccessToken().finally(() => {
          isRefreshingRef.current = false;
        });
      }

      // Show warning only if very close to expiry AND refresh has failed
      const shouldShowWarning =
        remaining <= WARNING_THRESHOLD_SECONDS &&
        remaining > 0 &&
        !warningDismissed &&
        refreshFailed;

      setShowWarning(shouldShowWarning);
    };

    // Check immediately
    checkSession();

    // Check every 10 seconds
    checkIntervalRef.current = setInterval(checkSession, 10000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [refreshAccessToken, warningDismissed, refreshFailed, isAuthenticated]);

  return {
    isAuthenticated,
    user,
    showWarning,
    timeUntilExpiry,
    setSession,
    refreshAccessToken,
    logout,
    dismissWarning,
    sendHeartbeat,
  };
}
